import {
  add,
  distance,
  EPS,
  lerp,
  normalize,
  perp,
  pointInPolygon,
  scale,
  sub,
  type Vec2,
} from '@indusketch/geometry';
import type { Canopy, Opening, ProjectDocument, Wall } from '../types.js';

export interface WallFrame {
  readonly length: number;
  /** Unit vector from start to end. */
  readonly dir: Vec2;
  /** Unit vector to the left of the wall direction (interior side of CCW halls). */
  readonly normal: Vec2;
}

export function wallFrame(wall: Pick<Wall, 'start' | 'end'>): WallFrame {
  const dir = normalize(sub(wall.end, wall.start));
  return { length: distance(wall.start, wall.end), dir, normal: perp(dir) };
}

export const pointAlongWall = (wall: Pick<Wall, 'start' | 'end'>, offset: number): Vec2 =>
  add(wall.start, scale(wallFrame(wall).dir, offset));

/** How far a wall is drawn past its start and end point to close the joints it is part of. */
export interface WallEnds {
  readonly start: number;
  readonly end: number;
}

/** Ends this close to another wall (beyond its face) still count as meeting it. */
const JOINT_TOLERANCE = 0.02;
/**
 * A wall ending against the side of another stops this far short of its far face: its end
 * would lie in that face and flicker through it.
 */
const T_JOINT_SINK = 0.002;

/**
 * Length the end at `p` must go on in direction `outward` to reach the far face of the walls it
 * meets there (just short of it in a T-joint, negative if it was drawn up to it); 0 where it
 * meets none or runs on in line with another wall.
 */
function jointReach(
  p: Vec2,
  outward: Vec2,
  wall: Wall,
  others: readonly Wall[],
  isShell: (w: Wall) => boolean,
): number {
  let reach: number | null = null;
  for (const w of others) {
    const g = wallFrame(w);
    if (g.length < EPS) continue;
    const c = outward.x * g.normal.x + outward.y * g.normal.y;
    // Nearly parallel: a wall continuing in line needs no closing.
    if (Math.abs(c) < 0.2) continue;
    const rel = sub(p, w.start);
    const across = rel.x * g.normal.x + rel.y * g.normal.y;
    const along = rel.x * g.dir.x + rel.y * g.dir.y;
    const half = w.thickness / 2;
    if (Math.abs(across) > half + JOINT_TOLERANCE) continue;
    const margin = wall.thickness / 2 + JOINT_TOLERANCE;
    if (along < -margin || along > g.length + margin) continue;
    // Into a building's shell only to its middle: on to its outer face the end would show
    // (and flicker) on the facade.
    // Against its side (not at a corner, where both ends make the outer edge) it stays inside.
    const side = along > margin && along < g.length - margin;
    const face = isShell(w) ? 0 : side ? half - T_JOINT_SINK : half;
    const raw = Math.min((face - across * Math.sign(c)) / Math.abs(c), 2 * w.thickness);
    // Only a T-joint pulls an end back; corners are only closed.
    const travel = side ? raw : Math.max(0, raw);
    reach = reach === null ? travel : Math.max(reach, travel);
  }
  return reach ?? 0;
}

const jointsCache = new WeakMap<object, Map<string, WallEnds>>();

/**
 * Drawn and room walls end at the point of their centre line, which leaves a notch at corners
 * and a gap where a wall ends at another one. Their ends are drawn on to the far face of the
 * walls they meet, so L-corners are filled and T-joints run flush. Shell walls of buildings
 * keep their own corners; stored lengths, dimensions and openings stay as they are.
 */
export function wallJoints(doc: Pick<ProjectDocument, 'walls' | 'buildings'>): Map<string, WallEnds> {
  const hit = jointsCache.get(doc.walls);
  if (hit) return hit;
  const all = Object.values(doc.walls);
  const out = new Map<string, WallEnds>();
  for (const wall of all) {
    if (wall.ownerId && doc.buildings[wall.ownerId]) continue;
    const f = wallFrame(wall);
    if (f.length < EPS) continue;
    const others = all.filter((w) => w.id !== wall.id && w.level === wall.level);
    const isShell = (w: Wall) => w.ownerId !== null && doc.buildings[w.ownerId] !== undefined;
    const start = jointReach(wall.start, scale(f.dir, -1), wall, others, isShell);
    const end = jointReach(wall.end, f.dir, wall, others, isShell);
    if (start !== 0 || end !== 0) out.set(wall.id, { start, end });
  }
  jointsCache.set(doc.walls, out);
  return out;
}

export type DoorSwing = NonNullable<Opening['swing']>;

/** How a door opens; without a setting the hinge is at the wall's start, opening to the left. */
export const doorSwing = (o: Pick<Opening, 'swing'>): DoorSwing =>
  o.swing ?? { hinge: 'start', side: 'left' };

export const VOID_POST = 0.3;
const VOID_POST_SPACING = 6;

/** Supports of an open wall part, as offsets from the opening's start (empty without posts). */
export function voidPostOffsets(o: Pick<Opening, 'type' | 'width' | 'posts'>): number[] {
  if (o.type !== 'void' || !o.posts) return [];
  const a = VOID_POST / 2;
  const b = o.width - VOID_POST / 2;
  if (b <= a) return [o.width / 2];
  const n = Math.ceil((b - a) / VOID_POST_SPACING);
  return Array.from({ length: n + 1 }, (_, i) => a + ((b - a) * i) / n);
}

/** Exterior side of a wall: away from the hall, office or room it belongs to. */
export const outwardSide = (
  wall: Pick<Wall, 'start' | 'end' | 'thickness'>,
  inside: readonly Vec2[] | null,
): 'left' | 'right' => outwardSwing(wall, inside)?.side ?? 'left';

export interface CanopyGeometry {
  /** Outline in plan: wall face first (a, b), then the front edge (c, d). */
  readonly outline: readonly [Vec2, Vec2, Vec2, Vec2];
  /** Absolute height of the underside at the wall and at the front edge. */
  readonly back: number;
  readonly front: number;
  /** Unit vector from the wall towards the front. */
  readonly out: Vec2;
  readonly dir: Vec2;
  readonly posts: readonly Vec2[];
}

/** Plan outline, heights and posts of a canopy on its wall (`base` = wall base elevation). */
export function canopyGeometry(
  wall: Pick<Wall, 'start' | 'end' | 'thickness'>,
  c: Pick<
    Canopy,
    'offsetFromWallStart' | 'width' | 'depth' | 'height' | 'pitchDeg' | 'side' | 'posts'
  >,
  base = 0,
): CanopyGeometry {
  const f = wallFrame(wall);
  const out = c.side === 'left' ? f.normal : scale(f.normal, -1);
  const face = (s: number) => add(add(wall.start, scale(f.dir, s)), scale(out, wall.thickness / 2));
  const a = face(c.offsetFromWallStart);
  const b = face(c.offsetFromWallStart + c.width);
  const d = add(a, scale(out, c.depth));
  const e = add(b, scale(out, c.depth));
  const back = base + c.height;
  const front = back - c.depth * Math.tan((c.pitchDeg * Math.PI) / 180);
  const posts: Vec2[] = [];
  if (c.posts) {
    const inset = 0.15;
    const n = Math.max(1, Math.ceil((c.width - 2 * inset) / VOID_POST_SPACING));
    for (let i = 0; i <= n; i++) {
      const s = c.offsetFromWallStart + inset + ((c.width - 2 * inset) * i) / n;
      posts.push(add(face(s), scale(out, c.depth - inset)));
    }
  }
  return { outline: [a, b, e, d], back, front, out, dir: f.dir, posts };
}

/**
 * Swing of a door opening away from `inside` (outline of the hall, office or room the wall
 * belongs to), as escape doors do. Null for walls without such an outline.
 */
export function outwardSwing(
  wall: Pick<Wall, 'start' | 'end' | 'thickness'>,
  inside: readonly Vec2[] | null,
): DoorSwing | null {
  if (!inside || inside.length < 3) return null;
  const f = wallFrame(wall);
  const probe = add(lerp(wall.start, wall.end, 0.5), scale(f.normal, wall.thickness / 2 + 0.3));
  return { hinge: 'start', side: pointInPolygon(probe, inside) ? 'right' : 'left' };
}

/** Wall box part in wall coordinates: `from`/`to` along the wall, `bottom`/`top` height. */
export interface WallSegment {
  readonly from: number;
  readonly to: number;
  readonly bottom: number;
  readonly top: number;
}

/**
 * Open wall parts of other walls on the same line (two halls built together, a free wall drawn
 * over a hall wall) open this wall there as well. Returned as openings of this wall.
 */
export function sharedVoids(
  doc: Pick<ProjectDocument, 'openings' | 'walls'>,
  wall: Wall,
): Opening[] {
  const f = wallFrame(wall);
  const along = (p: Vec2) => (p.x - wall.start.x) * f.dir.x + (p.y - wall.start.y) * f.dir.y;
  const out: Opening[] = [];
  for (const v of Object.values(doc.openings)) {
    if (v.type !== 'void' || v.wallId === wall.id) continue;
    const other = doc.walls[v.wallId];
    if (!other || other.level !== wall.level) continue;
    const g = wallFrame(other);
    if (Math.abs(f.dir.x * g.dir.x + f.dir.y * g.dir.y) < 0.999) continue;
    const apart = Math.abs(
      (other.start.x - wall.start.x) * f.normal.x + (other.start.y - wall.start.y) * f.normal.y,
    );
    if (apart > (wall.thickness + other.thickness) / 2 + 0.05) continue;
    const a = along(pointAlongWall(other, v.offsetFromWallStart));
    const b = along(pointAlongWall(other, v.offsetFromWallStart + v.width));
    const from = Math.max(0, Math.min(a, b));
    const to = Math.min(f.length, Math.max(a, b));
    if (to - from < 0.05) continue;
    out.push({
      ...v,
      id: `${v.id}@${wall.id}`,
      wallId: wall.id,
      offsetFromWallStart: from,
      width: to - from,
      height: Math.min(v.height, wall.height - v.sillHeight),
      posts: false,
    });
  }
  return out;
}

export function openingsOfWall(doc: Pick<ProjectDocument, 'openings'>, wallId: string): Opening[] {
  return Object.values(doc.openings)
    .filter((o) => o.wallId === wallId)
    .sort((a, b) => a.offsetFromWallStart - b.offsetFromWallStart);
}

/**
 * Splits a wall into solid boxes around its openings: full-height pieces between openings
 * plus a parapet below and a lintel above each opening. Replaces boolean (CSG) cutting,
 * which is fragile and expensive in real time.
 */
export function wallSegments(wall: Wall, openings: readonly Opening[]): WallSegment[] {
  const length = distance(wall.start, wall.end);
  const h = wall.height;
  // Openings may be stacked (windows on several floors), so the wall is cut into columns at
  // every opening edge; each column is solid except where openings cross it.
  const cuts = [
    ...new Set([
      0,
      length,
      ...openings.flatMap((o) => [o.offsetFromWallStart, o.offsetFromWallStart + o.width]),
    ]),
  ]
    .map((x) => Math.min(Math.max(x, 0), length))
    .sort((a, b) => a - b);
  const out: WallSegment[] = [];
  let prev: { key: string; pieces: WallSegment[] } | null = null;
  for (let i = 1; i < cuts.length; i++) {
    const from = cuts[i - 1] ?? 0;
    const to = cuts[i] ?? 0;
    if (to <= from + EPS) continue;
    const crossing = openings
      .filter(
        (o) => o.offsetFromWallStart < to - EPS && o.offsetFromWallStart + o.width > from + EPS,
      )
      .sort((a, b) => a.sillHeight - b.sillHeight);
    const key = crossing.map((o) => o.id).join('|');
    // Neighbouring columns crossed by the same openings are merged into one box.
    if (prev && prev.key === key) {
      prev.pieces = prev.pieces.map((p) => ({ ...p, to }));
      continue;
    }
    if (prev) out.push(...prev.pieces);
    const pieces: WallSegment[] = [];
    let bottom = 0;
    for (const o of crossing) {
      if (o.sillHeight > bottom + EPS)
        pieces.push({ from, to, bottom, top: Math.min(o.sillHeight, h) });
      bottom = Math.max(bottom, o.sillHeight + o.height);
    }
    if (bottom < h - EPS) pieces.push({ from, to, bottom, top: h });
    prev = { key, pieces };
  }
  if (prev) out.push(...prev.pieces);
  return out;
}

/**
 * What of a wall is really there (heights above its base): its own openings and the open parts
 * of walls on the same line are left out. Walking, collisions and docking use this.
 */
export function wallSolidParts(
  doc: Pick<ProjectDocument, 'openings' | 'walls'>,
  wall: Wall,
  /** Further open parts, e.g. the floors an office's exterior wall is open on. */
  extraVoids: readonly Opening[] = [],
): WallSegment[] {
  return wallSegments(wall, [
    ...openingsOfWall(doc, wall.id),
    ...sharedVoids(doc, wall),
    ...extraVoids,
  ]);
}

/** Plan view convention: every opening (doors and windows) interrupts the wall hatch. */
export function wallPlanSpans(wall: Wall, openings: readonly Opening[]): [number, number][] {
  const length = distance(wall.start, wall.end);
  const spans: [number, number][] = [];
  let cursor = 0;
  for (const o of [...openings].sort((a, b) => a.offsetFromWallStart - b.offsetFromWallStart)) {
    if (o.offsetFromWallStart > cursor + EPS) spans.push([cursor, o.offsetFromWallStart]);
    cursor = Math.max(cursor, o.offsetFromWallStart + o.width);
  }
  if (length > cursor + EPS) spans.push([cursor, length]);
  return spans;
}

/** Clamps an opening offset so the opening stays within its wall. */
export function clampOpeningOffset(wall: Wall, width: number, offset: number): number {
  const length = distance(wall.start, wall.end);
  return Math.min(Math.max(offset, 0), Math.max(length - width, 0));
}

/** Free intervals of a wall, i.e. the parts not occupied by other openings. */
function freeIntervals(length: number, others: readonly Opening[]): [number, number][] {
  const sorted = [...others].sort((a, b) => a.offsetFromWallStart - b.offsetFromWallStart);
  const out: [number, number][] = [];
  let cursor = 0;
  for (const o of sorted) {
    if (o.offsetFromWallStart > cursor) out.push([cursor, o.offsetFromWallStart]);
    cursor = Math.max(cursor, o.offsetFromWallStart + o.width);
  }
  if (length > cursor) out.push([cursor, length]);
  return out;
}

/**
 * Offset closest to `preferred` where an opening of `width` fits without overlapping the
 * others, or null if the wall has no gap wide enough. This is what lets users add a second
 * or third gate to a wall without first moving the existing ones.
 */
export function freeOpeningOffset(
  wall: Wall,
  others: readonly Opening[],
  width: number,
  preferred: number,
): number | null {
  const length = distance(wall.start, wall.end);
  let best: number | null = null;
  for (const [a, b] of freeIntervals(length, others)) {
    if (b - a < width - EPS) continue;
    const candidate = Math.min(Math.max(preferred, a), b - width);
    if (best === null || Math.abs(candidate - preferred) < Math.abs(best - preferred))
      best = candidate;
  }
  return best;
}

/** Openings whose height range meets [sill, sill + height]; only these can be in the way. */
export function sameHeightOpenings<T extends Pick<Opening, 'sillHeight' | 'height'>>(
  others: readonly T[],
  sill: number,
  height: number,
): T[] {
  return others.filter(
    (o) => o.sillHeight < sill + height - EPS && sill < o.sillHeight + o.height - EPS,
  );
}

/** Whether an opening of `width` at `offset` would overlap any of `others` on the same wall. */
export function openingOverlaps(
  others: readonly Opening[],
  width: number,
  offset: number,
): boolean {
  return others.some(
    (o) =>
      offset < o.offsetFromWallStart + o.width - EPS &&
      o.offsetFromWallStart < offset + width - EPS,
  );
}

export interface OpeningsSlide {
  /** New distance from the wall start per opening. */
  readonly offsets: ReadonlyMap<string, number>;
  /** Shift along each wall (by wall id), e.g. for the preview. */
  readonly shifts: ReadonlyMap<string, number>;
  /** The group would run into an opening that is not part of it; nothing should move. */
  readonly blocked: boolean;
}

/**
 * Several openings slide together, each along its own wall and all by the same distance per
 * wall, so their spacing stays (e.g. a window row on the ground and upper floor). `along`
 * gives the wanted shift on a wall; the group stops at the wall ends.
 */
export function slideOpenings(
  doc: Pick<ProjectDocument, 'openings' | 'walls'>,
  ids: readonly string[],
  along: (frame: WallFrame) => number,
  step: number | null = null,
): OpeningsSlide | null {
  const moving = new Set(ids);
  const byWall = new Map<string, Opening[]>();
  for (const id of ids) {
    const o = doc.openings[id];
    if (o) byWall.set(o.wallId, [...(byWall.get(o.wallId) ?? []), o]);
  }
  if (byWall.size === 0) return null;
  const offsets = new Map<string, number>();
  const shifts = new Map<string, number>();
  let blocked = false;
  for (const [wallId, group] of byWall) {
    const wall = doc.walls[wallId];
    if (!wall) continue;
    const f = wallFrame(wall);
    let shift = along(f);
    if (step) shift = Math.round(shift / step) * step;
    const lo = Math.max(...group.map((o) => -o.offsetFromWallStart));
    const hi = Math.min(...group.map((o) => f.length - o.offsetFromWallStart - o.width));
    shift = Math.min(Math.max(shift, lo), Math.max(hi, lo));
    shifts.set(wallId, shift);
    const others = Object.values(doc.openings).filter(
      (o) => o.wallId === wallId && !moving.has(o.id),
    );
    for (const o of group) {
      const offset = o.offsetFromWallStart + shift;
      offsets.set(o.id, offset);
      if (openingOverlaps(sameHeightOpenings(others, o.sillHeight, o.height), o.width, offset))
        blocked = true;
    }
  }
  return { offsets, shifts, blocked };
}

/**
 * New places for openings spread evenly on their walls: per wall the chosen ones keep their
 * order and size and get equal gaps between each other and to both wall ends.
 */
export function evenOpeningOffsets(
  doc: Pick<ProjectDocument, 'openings' | 'walls'>,
  ids: readonly string[],
): Map<string, number> {
  const byWall = new Map<string, Opening[]>();
  for (const id of ids) {
    const o = doc.openings[id];
    if (o) byWall.set(o.wallId, [...(byWall.get(o.wallId) ?? []), o]);
  }
  const out = new Map<string, number>();
  for (const [wallId, group] of byWall) {
    const wall = doc.walls[wallId];
    if (!wall) continue;
    const sorted = [...group].sort((a, b) => a.offsetFromWallStart - b.offsetFromWallStart);
    const length = distance(wall.start, wall.end);
    const gap = (length - sorted.reduce((s, o) => s + o.width, 0)) / (sorted.length + 1);
    if (gap < 0) continue;
    let cursor = gap;
    for (const o of sorted) {
      out.set(o.id, cursor);
      cursor += o.width + gap;
    }
  }
  return out;
}

/** Range in which an opening can slide without hitting its neighbours or the wall ends. */
export function openingSlideRange(
  wall: Wall,
  openings: readonly Opening[],
  id: string,
): [number, number] {
  const me = openings.find((o) => o.id === id);
  const length = distance(wall.start, wall.end);
  if (!me) return [0, length];
  let min = 0;
  let max = length - me.width;
  for (const o of openings) {
    if (o.id === id) continue;
    if (o.offsetFromWallStart + o.width <= me.offsetFromWallStart + EPS)
      min = Math.max(min, o.offsetFromWallStart + o.width);
    else if (o.offsetFromWallStart >= me.offsetFromWallStart + me.width - EPS)
      max = Math.min(max, o.offsetFromWallStart - me.width);
  }
  return [min, Math.max(min, max)];
}

/** Point on the wall centerline at parameter t ∈ [0,1]. */
export const wallPointAt = (wall: Pick<Wall, 'start' | 'end'>, t: number): Vec2 =>
  lerp(wall.start, wall.end, t);
